import { useState } from "react";
import { MainMenu } from "../components/MainMenu";
import { LoginRegisterModal } from "../components/modals/LoginRegisterModal";
import { ProjectListCard } from "../components/ProjectListCard";
import { getDomainCfg, store } from "../core/state";
import { ProjectListEntry } from "../core/types";
import { CLOUD_PROJECT_LIST, CLOUD_USER } from "../features/cloudstorage/cloud";

import { redirectToEditorProject, redirectToPortalRequested, redirectToQuestView } from "../features/redirect";

import {  translate } from "../core/i18n";
import { LIST_VIEW_NAME } from "../core/constants";
import { Onboarding } from "../components/Onboarding";
import { loadCurrentProjectList, loadProjectList } from "../core/code/loader";
import { findCategoriesFromProjects, findProjectsWithCategory, findProjectsWithoutCategory } from "../core/project/projectslist";
import { getAppCfg } from "../core/app-cfg";
import { HasCardsModal } from "../components/modals/HasCardsModal";
import { getQuestList } from "../core/quest/questloader";
import { ProjectListAppCards } from "../components/ProjectListAppCards";
import { ProjectListTable } from "../components/ProjectListTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export function ProjectListView() {
  const [listView] = store.useState(LIST_VIEW_NAME);
  const [cloudUser] = store.useState(CLOUD_USER);
  const [cloudProjectList] = store.useState(CLOUD_PROJECT_LIST)
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentView, setCurrentView] = useState(listView?.cfg?.defaultView || "appCards");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Callback to reload project list after deletion
  const handleProjectDeleted = async () => {
    await loadProjectList();
    setRefreshTrigger(prev => prev + 1); // Force re-render
  };

  const projectCardClicked = (project : ProjectListEntry) => {
    if (project.id === "NEW") {
      redirectToPortalRequested();
    } else if (project.status === "quest") {
      redirectToQuestView(project.id);
    } else if (project.status === "directory") { 
      setSelectedCategory(project.name);
    } else if (project.id === "back") { 
      setSelectedCategory("");
    }else {
      redirectToEditorProject(project.id);
    }
  }

  if (cloudUser.loggedIn && !cloudProjectList.list && !loading) {
    setLoading(true);
  } else if (cloudUser.loggedIn && cloudProjectList.list && loading) {
    setLoading(false);
  }

  let projectList = loadCurrentProjectList(cloudProjectList, cloudUser);  
  console.log('ProjectListView:', projectList);
  const categoryFilter = getAppCfg().category || selectedCategory;
  projectList = findProjectsWithCategory(projectList, categoryFilter);
  if (listView?.cfg?.showCategoryDirectories === true && (selectedCategory === "" && categoryFilter === "")) {    
    let projectListWithOutCategory = findProjectsWithoutCategory(projectList);
    
    let categories = findCategoriesFromProjects();
    
    if (listView?.cfg?.showEmptyCategories === false) {
      const filteredCategories = [];
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        const projectsWithCategory = findProjectsWithCategory(projectList, category);

        if (projectsWithCategory.length > 0) {
          filteredCategories.push(category);
        }
      }
      categories = filteredCategories;
    }
      
    const directories = categories.map(category => {    
        return directoryButton(category);    
    });
    projectList = projectListWithOutCategory;
    projectList.unshift(...directories);
  }

  let questList = getQuestList();
  const quests = questList?.map(quest => {    
    return questButton(quest);    
  });
  projectList.unshift(...quests);


  if (listView?.cfg?.showCategoryDirectories === true && selectedCategory !== "") {
    projectList.unshift(backButton());
  } else {
    //check if onboarding is already contained in projectList
    if (getDomainCfg().product.onboarding !== undefined && getDomainCfg().viewSettings.features.hasOnboarding === true) {
      const onboardingProject = projectList.find(project => project.id === "ONBO");
      if (!onboardingProject) {
        projectList.unshift(onboardingProjectButton());
      }

    }
    projectList.unshift(newProjectButton());
  }

  const viewIcon= (currentView === "appCards" ? "table" : "mobile-screen-button") as any;
  const viewIconText = currentView === "appCards" ? translate("view-switch-table-tooltip") : translate("view-switch-cards-tooltip");
  const viewSwitch = listView?.cfg?.viewSwitch ? (
    <button 
      className="btn btn-icon menu-btn" 
      onClick={() => setCurrentView(currentView === "appCards" ? "table" : "appCards")}
      title={viewIconText}
      style={{ marginRight: '10px' }} 
    >
      <FontAwesomeIcon icon={viewIcon} />
    </button>
  ) : null;

  const menu = (
    <div className="d-inline-flex align-items-center">
      {viewSwitch}
      <MainMenu withProject={false} menuitems={listView?.cfg?.menus?.mainmenu} />
    </div>
  );

  const loadingList = (<div className="row" id="loading">
    <div className="col-12 text-center">
      <div className="spinner-grow text-warning" role="status">
        <span className="sr-only">{ translate("view-launch-loading")}</span>
      </div>
      <p>{ translate("view-projects-apps-loading-text") }</p>
    </div>
  </div>);
    
    const renderedProjects = currentView === "appCards" ? (
      <ProjectListAppCards projects={projectList} onProjectClick={projectCardClicked} onProjectDeleted={handleProjectDeleted} />
    ) : (
      <ProjectListTable projects={projectList} onProjectClick={projectCardClicked} onProjectDeleted={handleProjectDeleted} />
    );
    


  const projectContainer = (<div className="row" id="projectcontainer">
    <div className="col-12">
      <div className="rounded-lg shadow p-3 mb-5 container-app-style scrollable-list">
        <div className="row row-cols-1" id="projects">
          { renderedProjects }
        </div>
      </div>
    </div>
  </div>);

  const header = listView?.cfg?.showHeader !== false ? (
    <>
      <div className="row">
        <div className="col">
          <p className="h3"><img src={getDomainCfg().product.logoUrl} className="imgcenter productLogo" max-height="57" alt={getDomainCfg().product.name} /></p>
          <p className="text-center">{translate("view-projects-header", getDomainCfg().product.name)}</p>
        </div>
      </div>
      <div className="row row-cols-1 text-end mb-10">
        <div className="col text-end">
          {menu}
        </div>
      </div>
    </>
  ) : null;

  return (<>
    <LoginRegisterModal />
    <HasCardsModal />
    <Onboarding view={LIST_VIEW_NAME}/>
    <div className="container text-center">
      {header}
      { loading && loadingList }
      { !loading && projectContainer }
    </div>
  </>);
}

function newProjectButton() {
  return {
    id: "NEW",
    name: translate("projects-new-action"),
    icon:"/assets/img/neu.png",
    status:"new"
  };
}

function onboardingProjectButton() {
  return {
    id: "ONBO",
    name: translate("projects-onboarding"),
    icon:"/assets/img/icons/VLOFONBO.png",
    status:"new"
  };
}

function questButton(quest) {
  return {
    id: quest.id,
    name: quest.description,
    icon: quest.icon || "/assets/img/quest.png",
    status:"quest"
  };
}

function directoryButton(category) {
  return {
    id: category,
    name: category,
    icon:"/assets/img/directory.png",
    status:"directory"
  };
}

function backButton() {
  return {
    id: "back",
    name: translate("projects-back"),
    icon:"/assets/img/back.png",
    status:"back"
  };
}
